import { useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import { useLazyQuery } from "@apollo/client";
import { SEARCH_DEBOUNCE_MS } from "../modules/ParticipantsConstants";

const useBatchSearch = ({
  ids,
  searchText,
  skip = false,
  searchQuery,
  mapSearchData,
  extractSearchIds,
  searchVariablesBuilder,
  onBeforeSearch,
}) => {
  const [searchFn, { data, loading }] = useLazyQuery(searchQuery, {
    fetchPolicy: "network-only",
  });

  // ---------------- ✅ REAL DEBOUNCE ----------------
  const [debouncedSearchText, setDebouncedSearchText] = useState(searchText);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchText]);

  // ---------------- ✅ STABLE IDS ----------------
  const idsKey = useMemo(() => ids.join(","), [JSON.stringify(ids)]);
  const stableIds = useMemo(() => ids, [idsKey]); // eslint-disable-line

  // ---------------- ✅ REQUEST DEDUPE ----------------
  const lastRequestKeyRef = useRef("");

  useEffect(() => {
    if (skip || _.isEmpty(debouncedSearchText)) return;

    const requestKey = `${debouncedSearchText}__${idsKey}`;

    // 🚫 prevent duplicate calls
    if (lastRequestKeyRef.current === requestKey) return;

    lastRequestKeyRef.current = requestKey;

    onBeforeSearch?.();

    const variables = searchVariablesBuilder(debouncedSearchText, stableIds);

    searchFn({ variables });
  }, [
    debouncedSearchText,
    idsKey,
    skip,
    searchFn,
    searchVariablesBuilder,
    onBeforeSearch,
    stableIds,
  ]);

  // ---------------- RESULTS ----------------
  const searchItems = useMemo(() => {
    if (!debouncedSearchText || !data) return null;
    return mapSearchData(data);
  }, [data, debouncedSearchText, mapSearchData]);

  const searchIds = useMemo(() => {
    if (!debouncedSearchText || !data) return null;
    return extractSearchIds(data);
  }, [data, debouncedSearchText, extractSearchIds]);

  return {
    searchItems,
    searchIds,
    isSearchLoading: loading,
  };
};

export default useBatchSearch;
